import { useToast } from '@components/toast/Toast'
import { Check, Copy, Share2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface ReferralCodeCardProps {
  title: string
  code: string | null
  type: 'instagram' | 'facebook'
  usageCount?: number
  clickCount?: number
  isExpired?: boolean
}

export function ReferralCodeCard({
  title,
  code,
  type,
  usageCount = 0,
  clickCount = 0,
  isExpired = false
}: ReferralCodeCardProps) {
  const { t } = useTranslation('settings')
  const toast = useToast()
  const [copied, setCopied] = useState(false)

  const shareUrl =
    type === 'facebook' ? `${window.location.origin}/ref?ref=${code}` : code

  const handleCopy = async () => {
    if (!code) return
    try {
      await navigator.clipboard.writeText(shareUrl ?? code)
      setCopied(true)
      toast.success({ message: t('referrals.codeCopied') })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error({ message: t('referrals.copyFailed') })
    }
  }

  const handleShare = async () => {
    if (!code) return
    const shareData = {
      title: t('referrals.shareTitle'),
      text: t('referrals.shareText'),
      url: shareUrl ?? code
    }
    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        await handleCopy()
      }
    } catch {
      // user cancelled or sharing failed
    }
  }

  if (!code) {
    return (
      <Card className="space-y-1 p-5">
        <h3 className="m-0 font-display text-base font-bold text-ink">
          {title}
        </h3>
        <p className="m-0 text-sm text-muted-foreground">
          {t('referrals.noCodeAvailable')}
        </p>
      </Card>
    )
  }

  return (
    <Card className={cn('flex flex-col gap-3 p-5', isExpired && 'opacity-60')}>
      <h3 className="m-0 font-display text-base font-bold text-ink">{title}</h3>
      <code className="break-all rounded-xl bg-ink/[0.05] px-3 py-2 font-mono text-sm text-ink">
        {type === 'facebook' ? `...ref=${code}` : code}
      </code>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {type === 'instagram'
            ? `${t('referrals.used')}: ${usageCount}`
            : `${t('referrals.clicks')}: ${clickCount}`}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghostDark"
            size="icon"
            onClick={handleCopy}
            disabled={isExpired}
            aria-label={
              type === 'facebook'
                ? t('referrals.copyLink')
                : t('referrals.copyCode')
            }
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghostDark"
            size="icon"
            onClick={handleShare}
            disabled={isExpired}
            aria-label={t('referrals.share')}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {isExpired && (
        <span className="text-xs font-medium text-destructive">
          {t('referrals.expired')}
        </span>
      )}
    </Card>
  )
}
